import React, { Component, Fragment } from "react";
import _ from "lodash";
import "./Scenario.css";
import type { RouteComponentProps } from "react-router";
import { Popconfirm, Tooltip } from "antd";
import { withCommas } from "../../helpers";
import {
  ArrowRightOutlined,
  DeleteOutlined,
  PlusSquareOutlined,
  MinusSquareOutlined,
  EditOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import type { RootState } from "../../reducers/index";
import { connect, ConnectedProps } from "react-redux";
import { Link } from "react-router-dom";
import type { LngLatBoundsLike } from "mapbox-gl";
import { withRouter } from "react-router-dom";
import bbox from "@turf/bbox";
import {
  actionScenarioGet,
  actionScenarioSave,
  actionBlockgroupValueEdit,
  actionScenarioTitleEdit,
  actionScenarioGeometryDelete,
  actionScenarioCanopyGoalEdit,
  actionBlockgroupSpeciesPropertyEdit,
  actionParcelSpeciesPropertyEdit,
  actionParcelAcresAddedEdit,
  actionFeatureSpeciesPropertyEdit,
  actionFeatureAcresAddedEdit,
} from "../../actions";
import {
  PARCEL_ZOOM,
  BLOCKGROUP_ZOOM,
  formatPercent,
} from "../../configs/constants";
import {
  BlockgroupRow,
  ParcelRow,
  FeatureRow,
  ScenarioTitle,
} from "./components";
import {
  Accordion,
  StatisticRow,
  StatisticCell,
  Statistic,
  IconButton,
} from "../../components";
import { parcelAddress, blockgroupBounds } from "../../helpers";
import type { Municipality, Blockgroup, Parcel } from "../../types";

const mapStateToProps = (state: RootState) => {
  if (state) {
    return state;
  } else {
    return null;
  }
};

const mapDispatchToProps = {
  actionScenarioGet,
  actionScenarioSave,
  actionBlockgroupValueEdit,
  actionScenarioTitleEdit,
  actionScenarioGeometryDelete,
  actionScenarioCanopyGoalEdit,
  actionBlockgroupSpeciesPropertyEdit,
  actionParcelSpeciesPropertyEdit,
  actionParcelAcresAddedEdit,
  actionFeatureSpeciesPropertyEdit,
  actionFeatureAcresAddedEdit,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface RouterProps {
  id: string;
}

type MyProps = PropsFromRedux & {
  draw: any;
  map: mapboxgl.Map;
  collapsed: boolean;
  scenarioDelete: any;
  toggleCollapse: any;
  hover: (val: Blockgroup | Parcel) => void;
  close: any;
} & RouteComponentProps<RouterProps>;

class Scenario extends Component<MyProps> {
  saveScenario: any;

  constructor(props: MyProps) {
    super(props);
    // Only save after 500ms of silence
    this.saveScenario = _.debounce(this._saveScenario, 500);
  }

  _saveScenario = () => {
    this.props.actionScenarioSave(this.props.scenario);
  };

  render() {
    const { scenario, user, map, collapsed } = this.props;
    return (
      <div className="scenario">
        <div className="scenario__header">
          <div className="scenario__header-cell scenario__header-cell--label">
            <ScenarioTitle
              scenario={scenario}
              actionScenarioTitleEdit={this.props.actionScenarioTitleEdit}
              saveScenario={this.saveScenario}
            />
          </div>
          <div className="scenario__header-cell">
            <Popconfirm
              placement="leftTop"
              title="Are you sure you want to delete this scenerio?"
              okText="Yes"
              cancelText="No"
              onConfirm={this.props.scenarioDelete}
            >
              <IconButton title="Delete scenario">
                <DeleteOutlined />
              </IconButton>
            </Popconfirm>
          </div>
          {!!scenario.municipalities.length && (
            <div className="scenario__header-cell">
              <IconButton
                title={collapsed ? "Expand item" : "Collapse item"}
                onClick={this.props.toggleCollapse}
              >
                {collapsed ? <PlusSquareOutlined /> : <MinusSquareOutlined />}
              </IconButton>
            </div>
          )}
          <div className="scenario__header-cell">
            <IconButton onClick={this.props.close}>
              <Tooltip
                placement="left"
                arrowPointAtCenter
                title="Close this scenario"
              >
                <CloseOutlined />
              </Tooltip>
            </IconButton>
          </div>
        </div>
        {!collapsed && (
          <div className="scenario__content">
            {scenario.municipalities &&
              scenario.municipalities.map((municipality: Municipality) => {
                return (
                  <div className="scenario__item" key={municipality.NAME}>
                    <div
                      id={municipality.NAME}
                      className="scenario__row scenario__row--municipality"
                    >
                      <Accordion
                        size="condensed"
                        heading={municipality.NAME.toLowerCase()}
                        link={municipality.NAME.toLowerCase()}
                        onDeleteClick={
                          user.id &&
                          (() => {
                            this.props.actionScenarioGeometryDelete({
                              type: "municipality",
                              id: municipality.NAME,
                            });
                            this.saveScenario();
                          })
                        }
                      >
                        <StatisticRow>
                          <StatisticCell label='Tree Canopy Cover %'>
                            <Statistic
                              label="Tree Canopy Cover (Percent)"
                              value={formatPercent(
                                municipality.TrCan_Pct
                              )}
                            />

                            <Statistic
                              label="Current Tree Equity Score"
                              value={municipality.tesctymean.toFixed()}
                            />

                            <Statistic
                              label="Possible tree Canopy Cover"
                              value={formatPercent(
                                municipality.PossTCPct
                              )}
                            />

                            <Statistic
                              label="Trees needed to reach a Tree Equity Score of 100"
                              value= {
                                (
                                  (municipality.TrCan_Pct < 0.4) && 
                                 withCommas((.4 - municipality.TrCan_Pct) * municipality.AREA_GEO * 10.7639 / 500
                                )
                                ) || (0)
                              }
                            />
                          </StatisticCell>
                        </StatisticRow>
                      </Accordion>
                    </div>
                    {municipality.blockgroups.map(
                      (blockgroup, index: number) => (
                        <Fragment key={index}>
                          <div
                            id={blockgroup.GEOID as string}
                            className="scenario__row scenario__row--blockgroup"
                            onMouseEnter={() => this.props.hover(blockgroup)}
                            onMouseMove={() => this.props.hover(blockgroup)}
                            onMouseLeave={() => this.props.hover(null)}
                          >
                            <Accordion
                              size="condensed"
                              subhead={"Block Group | " + blockgroup.GEOID}
                              onZoomClick={() => {
                                map.fitBounds(
                                  blockgroupBounds(blockgroup.GEOID)
                                );
                              }}
                              onDeleteClick={
                                user.id &&
                                (() => {
                                  this.props.actionScenarioGeometryDelete({
                                    type: "blockgroup",
                                    id: blockgroup.GEOID,
                                  });
                                  this.saveScenario();
                                })
                              }
                            >
                              <BlockgroupRow
                                blockgroup={blockgroup}
                                onChange={() => {
                                  this.saveScenario();
                                }}
                                {...this.props}
                              />
                            </Accordion>
                          </div>
                          {blockgroup.parcels.map((parcel, index: number) => {
                            return (
                              <div
                                id={parcel.PARCEL_ID.toString()}
                                key={index}
                                className="scenario__row scenario__row--parcel"
                                onMouseEnter={() => this.props.hover(parcel)}
                                onMouseMove={() => this.props.hover(parcel)}
                                onMouseLeave={() => this.props.hover(null)}
                              >
                                <Accordion
                                  size="condensed"
                                  subhead={`Parcel | ` + parcelAddress(parcel)}
                                  onDeleteClick={
                                    user.id &&
                                    (() => {
                                      this.props.actionScenarioGeometryDelete({
                                        type: "parcel",
                                        id: parcel.PARCEL_ID,
                                      });
                                      this.saveScenario();
                                    })
                                  }
                                >
                                  <ParcelRow
                                    parcel={parcel}
                                    onChange={() => {
                                      this.saveScenario();
                                    }}
                                    {...this.props}
                                  />
                                </Accordion>
                              </div>
                            );
                          })}
                          {(blockgroup.features || []).map((feature, index) => {
                            return (
                              <div
                                id={feature.properties.id}
                                key={index}
                                className="scenario__row scenario__row--custom-area"
                              >
                                <Accordion
                                  size="condensed"
                                  subhead={`Custom Area | ${feature.properties.name}`}
                                  onZoomClick={() => {
                                    map.fitBounds(
                                      bbox(feature) as LngLatBoundsLike
                                    );
                                  }}
                                  onDeleteClick={
                                    user.id &&
                                    (() => {
                                      this.props.actionScenarioGeometryDelete({
                                        type: "feature",
                                        id: feature.properties.id,
                                      });
                                      this.saveScenario();
                                    })
                                  }
                                >
                                  <FeatureRow
                                    feature={feature}
                                    onChange={() => {
                                      this.saveScenario();
                                      this.forceUpdate();
                                    }}
                                    {...this.props}
                                  />
                                </Accordion>
                              </div>
                            );
                          })}
                        </Fragment>
                      )
                    )}
                  </div>
                );
              })}
          </div>
        )}
        <div className="scenario__footer">
          <div className="scenario__note">
            Click on a{" "}
            <button
              className="button-inline"
              onClick={() => map.zoomTo(BLOCKGROUP_ZOOM + 1)}
            >
              Blockgroup
            </button>{" "}
            or{" "}
            <button
              className="button-inline"
              onClick={() => map.zoomTo(PARCEL_ZOOM + 1)}
            >
              Parcel
            </button>{" "}
            and add it your scenario. Or create a Custom Area with the{" "}
            <button
              className="button-inline"
              onClick={() => {
                map.zoomTo(16);
                this.props.draw.changeMode("draw_polygon");
              }}
            >
              <EditOutlined /> draw tool
            </button>
            .
          </div>
          {!!scenario.municipalities.length && (
            <div className="scenario__footer-cta">
              <Link
                className="button--cta"
                to={`/scenario/${this.props.match.params.id}/report`}
              >
                <span className="button__label">Scenario Impact Report</span>
                <ArrowRightOutlined />
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const ScenarioContainer = connector(Scenario);

export default withRouter(ScenarioContainer);
